<script lang="ts" context="module">
	import SmoreLogo from "./SmoreLogo.svg";
	import { getShowContext } from "./showContext";
	import AccessibilityButton from "./AccessibilityButton.svelte";
	import ShareButton from "./ShareButton.svelte";
	import TranslateButton from "./TranslateButton.svelte";
	import { formatKMBT } from "$lib/components/utils/numberUtils";
</script>

<script lang="ts">
	const context = getShowContext();
	let isSharePopupOpen: boolean = false;
	let isTranslatePopupOpen: boolean = false;
	let isAccessibilityPopupOpen: boolean = false;

	const showLogo = context.brandingMode !== "none";

	let showMiddleBarWithViewCount =
		(context.showTranslationOptions || context.showSharingOptions || context.showAccessibilityOptions) && context.showViewCount;
	let showMiddleBarOnly =
		(context.showTranslationOptions || context.showSharingOptions || context.showAccessibilityOptions) && !context.showViewCount;

	let toolbarContainer: HTMLDivElement;
	let showPageToolbarContentHeight: number;

	$: if (toolbarContainer) {
		calculateToolbarHeight();
		// There is an issue with the material icons, that causes a gap between the toolbar and the rest of the content.
		// This is caused by the way we calculate the height and set it to the css variable,
		// since it takes time to download the material-icons font and use it by the browser (looks like it shows the text instead of the icon).
		// Therefore we introduce this workaround that fixes that and we still have a slight jump in the UI
		// but we don't have the gap anymore.
		setTimeout(calculateToolbarHeight, 400);
	}

	function calculateToolbarHeight() {
		// Warning: Use "getBoundingClientRect().height" instead of the "offsetHeight" property as
		// 					the latter returns a rounded integer instead of an accurate float.
		showPageToolbarContentHeight = toolbarContainer.getBoundingClientRect().height;
		document.body.style.setProperty("--show-page-toolbar-height", showPageToolbarContentHeight + "px");
	}
</script>

{#if !context.hideShowPageTopBar}
	<!-- Placeholder for the show-page toolbar, so everything is pushed down  -->
	<div style="height: var(--show-page-toolbar-height, 60px);" />

	<div
		bind:this={toolbarContainer}
		class="toolbar ada-bg-white fixed left-0 right-0 top-0 z-50 m-auto h-12 w-fit max-w-full rounded-b-lg border-t-4 bg-white px-4 shadow-lg filter">
		<div class="content mx-auto flex h-full items-center justify-between">
			{#if showLogo}
				<div aria-hidden="true" class="clickable -mt-1 flex-shrink-0">
					<a href="/?s=logo" class="mt-1.5 inline-block h-full">
						<img class="w-24" src={SmoreLogo} alt="Smore logo" />
					</a>
				</div>
			{/if}

			<div
				class="-mt-2 flex h-full items-center gap-x-6"
				class:ml-6={showMiddleBarOnly && showLogo}
				class:mx-8={showMiddleBarWithViewCount && showLogo}
				class:mr-8={showMiddleBarWithViewCount && !showLogo}
				class:sm:mx-6={showMiddleBarWithViewCount && showLogo}
				class:sm:mr-6={(showMiddleBarWithViewCount && !showLogo) || (showLogo && context.showViewCount && !showMiddleBarOnly)}
				class:md:mx-10={showMiddleBarWithViewCount && showLogo}
				class:md:mr-6={showMiddleBarWithViewCount && !showLogo}>
				{#if context.showSharingOptions}
					<!-- This wrapper is needed for the divider border color -->
					<div class="flex h-full w-full items-center">
						<!-- This wrapping div is needed for the active border color -->
						<div class="menu-button flex h-full w-full items-center border-t-4" class:active={isSharePopupOpen}>
							<ShareButton bind:isOpen={isSharePopupOpen} />
						</div>
					</div>
				{/if}
				{#if context.showTranslationOptions}
					<div class="flex h-full w-full items-center">
						<div class="menu-button flex h-full w-full items-center border-t-4" class:active={isTranslatePopupOpen}>
							<TranslateButton bind:isOpen={isTranslatePopupOpen} />
						</div>
					</div>
				{/if}
				{#if context.showAccessibilityOptions}
					<div class="flex h-full w-full items-center">
						<div
							class="menu-button ada-menu-button flex h-full w-full items-center rounded-br-lg border-t-4"
							class:active={isAccessibilityPopupOpen}>
							<AccessibilityButton bind:isOpen={isAccessibilityPopupOpen} />
						</div>
					</div>
				{/if}
			</div>
			{#if context.showViewCount}
				<div
					role="text"
					aria-label="View count: {formatKMBT(context.viewCount)}"
					class="notranslate -mt-2 flex h-full w-full select-none items-center rounded border-t-4 text-15 font-bold text-nxgray-600">
					<span aria-hidden="true" class="material-icons notranslate mr-0.5 text-18">remove_red_eye</span>
					<span class="text-15 font-bold">{formatKMBT(context.viewCount)}</span>
				</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	:global(.translated-rtl .toolbar),
	:global(.translated-ltr .toolbar) {
		/* Add margin for the Google Translate widget */
		top: 40px;
	}

	.menu-button:hover,
	.menu-button:not(.active):hover {
		border-color: theme("borderColor.nxgray.300");
	}
	.menu-button.active {
		border-color: theme("borderColor.nxgray.500");
	}
</style>
